import { supabase } from '../services/supabase';

/**
 * Claim from the page (JT, 27 Oct 2026): the "claim it free" bar on an
 * unclaimed shoutout.fans page opens /start?claim=<slug>. Once the visitor has
 * signed up or signed in, page_claim_start gives them that page (a normal
 * claim with no onboarding token; the server checks everything).
 */

/** Survives the email/OTP round-trip and reloads. */
export const PAGE_CLAIM_STORAGE_KEY = 'page_claim_slug';

/** A username as the server would match it, or null. */
export function normalizeClaimSlug(raw: string | null | undefined): string | null {
  const s = (raw ?? '').trim().toLowerCase();
  return /^[a-z0-9._-]{1,64}$/.test(s) ? s : null;
}

export type PageClaimPrefill = {
  username?: string | null;
  temp_full_name?: string | null;
  full_name?: string | null;
  temp_avatar_url?: string | null;
  pricing?: number | null;
  categories?: string[] | null;
  targeting_category?: string | null;
  selected_shoutout_types?: string[] | null;
  skip_promo_video?: boolean | null;
  onboarding_completed?: boolean | null;
};

export type PageClaimResult =
  | { ok: true; talentId: string; resumed: boolean; prefill: PageClaimPrefill }
  | { ok: false; error: string };

export async function startPageClaim(slug: string): Promise<PageClaimResult> {
  try {
    const { data, error } = await supabase.rpc('page_claim_start', { p_slug: slug });
    if (error) return { ok: false, error: 'request_failed' };
    const d = (data ?? {}) as { ok?: unknown; error?: unknown; talent_id?: unknown; resumed?: unknown; prefill?: unknown };
    if (d.ok === true && typeof d.talent_id === 'string') {
      return {
        ok: true,
        talentId: d.talent_id,
        resumed: d.resumed === true,
        prefill: (d.prefill && typeof d.prefill === 'object' ? d.prefill : {}) as PageClaimPrefill,
      };
    }
    return { ok: false, error: typeof d.error === 'string' ? d.error : 'request_failed' };
  } catch {
    return { ok: false, error: 'request_failed' };
  }
}

/** What to tell them when the page couldn't be claimed. DRAFT copy. */
export function pageClaimErrorMessage(code: string): string {
  switch (code) {
    case 'already_claimed':
      return 'Someone has already claimed this page. If it’s yours, email support@shoutout.us and we’ll sort it out.';
    case 'has_page':
      return 'You already have a ShoutOut page, so we’ve opened yours.';
    case 'too_many':
    case 'busy':
      return 'We couldn’t claim this page right now. Please try again later, or email support@shoutout.us.';
    case 'view_as':
      return 'Not available while viewing as a creator.';
    case 'off':
    case 'not_claimable':
      return 'This page can’t be claimed here. Email support@shoutout.us and we’ll help.';
    default:
      return 'We couldn’t claim this page. Please try again, or email support@shoutout.us.';
  }
}
